import { getApiBaseUrl, isMockModeForced } from "../../services/httpClient";
import { fetchAllBrands, fetchAllCategories, fetchAllProducts } from "./adminApi";
import {
  normalizeBrand,
  normalizeCategory,
  normalizeProductRecord,
  normalizeProductRecords,
} from "./adminTransformers";
import {
  buildMockAdminSnapshot,
  createDefaultSettings,
  createSeedCustomers,
  createSeedOrders,
} from "./mockAdminData";

const STORAGE_KEY = "grozykart-admin-workspace-v2";

function readPersistedSnapshot() {
  if (typeof window === "undefined" || !window.localStorage) {
    return null;
  }

  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return null;
    }

    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") {
      return null;
    }

    return parsed;
  } catch (error) {
    return null;
  }
}

function enrichSource({
  mode,
  label,
  description,
  apiAvailable = false,
  errorMessage = "",
}) {
  return {
    mode,
    label,
    description,
    apiAvailable,
    apiBaseUrl: getApiBaseUrl(),
    errorMessage,
    hasLocalDraft: false,
    lastLoadedAt: new Date().toISOString(),
  };
}

function buildOperationalData(products, persistedState) {
  const settings = persistedState?.settings || createDefaultSettings();
  const customers = Array.isArray(persistedState?.customers)
    ? persistedState.customers
    : createSeedCustomers();
  const orders = Array.isArray(persistedState?.orders)
    ? persistedState.orders
    : createSeedOrders(products, customers, settings.orderPrefix);

  return {
    settings,
    customers,
    orders,
  };
}

function buildSnapshotFromApi({ categories, brands, products }, persistedState) {
  const categoryMap = new Map(categories.map((category) => [category.id, category]));
  const brandMap = new Map(brands.map((brand) => [brand.id, brand]));
  const normalizedProducts = products.flatMap((item) =>
    normalizeProductRecords(item, categoryMap, brandMap)
  );

  return {
    products: normalizedProducts,
    categories,
    brands,
    ...buildOperationalData(normalizedProducts, persistedState),
  };
}

function getSettledValue(result, fallbackValue) {
  return result.status === "fulfilled" ? result.value : fallbackValue;
}

function getSettledError(result) {
  return result.status === "rejected" ? result.reason?.message || "Unknown API error." : "";
}

export async function loadAdminSnapshot() {
  const persistedState = readPersistedSnapshot();
  const fallbackSnapshot = buildMockAdminSnapshot();

  if (isMockModeForced()) {
    return {
      ...fallbackSnapshot,
      brands: [],
      source: enrichSource({
        mode: "seed",
        label: "Mock mode enabled",
        description:
          "Mock mode is forced through PARCEL_PUBLIC_USE_MOCK_DATA=true, so the admin is running on local seed data.",
      }),
    };
  }

  const [categoriesResult, brandsResult, productsResult] = await Promise.allSettled([
    fetchAllCategories(),
    fetchAllBrands(),
    fetchAllProducts(),
  ]);

  const categoriesPayload = getSettledValue(categoriesResult, []);
  const brandsPayload = getSettledValue(brandsResult, []);
  const productsPayload = getSettledValue(productsResult, []);

  const categories = categoriesPayload.map(normalizeCategory);
  const brands = brandsPayload.map(normalizeBrand);
  const snapshot = buildSnapshotFromApi(
    {
      categories,
      brands,
      products: productsPayload,
    },
    persistedState
  );

  const apiErrors = [
    getSettledError(categoriesResult),
    getSettledError(brandsResult),
    getSettledError(productsResult),
  ].filter(Boolean);

  if (!categories.length && !brands.length && !productsPayload.length) {
    return {
      ...fallbackSnapshot,
      source: enrichSource({
        mode: "seed",
        label: "Seeded workspace",
        description:
          "The product-service admin APIs could not be reached, so the workspace started from local seed data.",
        apiAvailable: false,
        errorMessage: apiErrors.join(" ") || "Unknown API connection error.",
      }),
    };
  }

  return {
    ...snapshot,
    source: enrichSource({
      mode: apiErrors.length ? "partial-admin-api" : "admin-api",
      label: apiErrors.length ? "Admin API partially connected" : "Admin API connected",
      description: apiErrors.length
        ? "Some admin API data loaded successfully, while one or more product-service endpoints failed."
        : "Products, categories, and brands are loaded from the product-service admin APIs.",
      apiAvailable: true,
      errorMessage: apiErrors.join(" "),
    }),
  };
}

export function persistAdminSnapshot(snapshot) {
  if (typeof window === "undefined" || !window.localStorage) {
    return;
  }

  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(snapshot));
  } catch (error) {
    return;
  }
}

export function clearPersistedAdminSnapshot() {
  if (typeof window === "undefined" || !window.localStorage) {
    return;
  }

  window.localStorage.removeItem(STORAGE_KEY);
}
